import {GenericFlavorlessStampSpawnFood} from "./genericFlavorlessStampSpawnFood.tsx";
import {useTranslation} from "react-i18next";
import {useBomb} from "../bomb/bomb.tsx";
import {FormEvent, useCallback, useContext, useState} from "react";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import {useQueryClient} from "@tanstack/react-query";
import {useAuthenticator} from "../auth/authenticator.tsx";
import {Box, FormControl, FormLabel, LinearProgress, Stack, Textarea, useTheme} from "@mui/joy";
import {PhotoInput, uploadPhoto} from "../photo.tsx";
import {ActionButton} from "../components/ActionButton.tsx";
import {ScrollableViewContext} from "../scrollableViewState.tsx";
import {NewStampForm} from "./newStampForm.tsx";

export interface TimelineStampInputProps {
    addStamp: (genfood: GenericFlavorlessStampSpawnFood) => Promise<unknown>,
}

export function TimelineStampInput({addStamp}: TimelineStampInputProps) {
    const {t} = useTranslation();
    const bomb = useBomb();

    const {scrollToBottom} = useContext(ScrollableViewContext);
    const {
        control,
        handleSubmit,
        reset: resetForm,
    } = useForm<NewStampForm>({
        defaultValues: {
            photo: null,
            caption: "",
        }
    });
    const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false);
    const [photoUploadProgress, setPhotoUploadProgress] = useState<number | null>(null);
    const [isBusy, setIsBusy] = useState<boolean>(false);
    const tqc = useQueryClient();
    const auther = useAuthenticator();
    const theme = useTheme();

    const spawn = useCallback<(form: NewStampForm) => Promise<unknown>>((form => {
        if (isBusy) {
            return Promise.resolve();
        } else {
            setIsBusy(true);
            return Promise.resolve()
                .then(() => {
                    if (form.photo) {
                        const photo = form.photo;
                        setIsUploadingPhoto(true);
                        return uploadPhoto({
                            authenticator: auther,
                            queryClient: tqc,
                            photo: photo,
                            onProgress: setPhotoUploadProgress,
                            onComplete: () => {
                                setIsUploadingPhoto(false);
                                setPhotoUploadProgress(null);
                            }
                        });
                    }
                })
                .then(() => {
                    return addStamp({photoId: form.photo?.id, caption: form.caption});
                })
                .then(() => {
                    setTimeout(() => {
                        scrollToBottom();
                    }, 0);
                    resetForm();
                })
                .catch((xc: unknown) => {
                    setIsUploadingPhoto(false);
                    const error = xc instanceof Error ? xc :
                        new Error("photo upload failed", {cause: xc});
                    bomb(error);
                })
                .finally(() => {
                    setIsBusy(false);
                });
        }
    }), [isBusy, auther, tqc, addStamp, resetForm, scrollToBottom, bomb]);
    const busyBlocker = isBusy ? {disabled: true} : {};

    const onValidSubmit: SubmitHandler<NewStampForm> = food => {
        void spawn(food);
    };

    const onSubmit = (evt: FormEvent<HTMLFormElement>) => {
        void (handleSubmit(onValidSubmit))(evt);
    };

    // <Box sx={{
    //     position: "sticky", ml: "0.6rem", mr: "4rem",
    //
    //     backgroundColor: "white", display: "flex",
    //     justifyContent: "center", alignItems: "center",
    //     bottom: 6, left: 6,
    // }}>
    return <Box sx={{
        display: "flex",
    }}>
        <Stack direction="column" spacing={1} sx={{
            p: 0.4, width: "100%",
            borderColor: theme.palette.primary[700], borderWidth: 3, borderStyle: "groove",
        }}>
            {isUploadingPhoto
                ? <LinearProgress sx={{width: "95%"}}
                                  color="primary" size="lg" thickness={16}
                                  determinate={!!photoUploadProgress}
                                  value={photoUploadProgress ?? undefined}
                />
                : null}
            <form onSubmit={onSubmit}>
                <Stack direction="row" sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    m: 0.8,
                }} spacing={3}>
                    <Controller control={control} name="photo"
                                render={({field}) =>
                                    <PhotoInput value={field.value}
                                                onChange={(evt) => {
                                                    // console.log("got photo input change");
                                                    field.onChange(evt);
                                                }}/>}/>
                    <Controller control={control} name="caption"
                                render={({field}) =>
                                    <FormControl sx={{flex: 1}}>
                                        <FormLabel>{t("stampComment")}</FormLabel>
                                        <Textarea minRows={2}
                                                  value={field.value}
                                                  onChange={field.onChange}/>
                                    </FormControl>
                                }/>
                    <ActionButton type="submit" size="lg" sx={{px: 4, py: 2, whiteSpace: "nowrap"}} {...busyBlocker}>
                        {t("add")}
                    </ActionButton>
                </Stack>
            </form>
        </Stack>
    </Box>
}